import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronUp, ChevronDown, FileText, FileSpreadsheet, Mail, MessageCircle, Phone } from 'lucide-react';
import * as XLSX from 'xlsx';
import {
    useGetLifePolicyEntriesQuery,
    useGetLifePremiumsDueQuery,
} from '../../../../../../services/redux/api/endpoints/lifePolicyEntryDashEndPoint';
import { useGetBirthdaysQuery } from '../../../../../../services/redux/api/endpoints/birthdayEndpoints';
import { useGetAnniversariesQuery } from '../../../../../../services/redux/api/endpoints/anniversaryEndpoints';
import { useGetGeneralRenewalsQuery } from '../../../../../../services/redux/api/endpoints/generalRenewalsEndpoints';
import DashboardListView from './DashboardListView';
import './DashboardDetail.css';
import CustomDatePickerDashboard from './CustomDatePickerDashboard';
import CommunicationMasterModal from '../../../../shared/CommunicationMasterModal';
import { toast } from 'react-toastify';
// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return String(date);
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

const toInputDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const calculateNumberOfPremiums = (commDate, fupDate, mode) => {
    if (!commDate || !fupDate) return 0;
    const c = new Date(commDate);
    const f = new Date(fupDate);
    if (isNaN(c.getTime()) || isNaN(f.getTime())) return 0;

    const diffMonths = Math.abs((f.getFullYear() - c.getFullYear()) * 12 + (f.getMonth() - c.getMonth()));
    const m = String(mode || '').trim().toUpperCase();

    let interval = 12; // default yearly
    if (m === 'MONTHLY' || m === 'M' || m === 'MLY' || m === 'SSS' || m === 'S' || m === 'SALARY') interval = 1;
    else if (m === 'QUARTERLY' || m === 'Q' || m === 'QLY') interval = 3;
    else if (m === 'HALFYEARLY' || m === 'HALFYEAR' || m === 'H' || m === 'HLY' || m === 'HALF-YEARLY' || m === 'HALF YEARLY') interval = 6;
    else if (m === 'YEARLY' || m === 'Y' || m === 'YEAR') interval = 12;

    return Math.ceil(diffMonths / interval);
};

const getDefaultDates = (filterType, tableKey) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let fromDate = new Date(today);
    let toDate = new Date(today);

    // ── NEXT_* filters → future range (Birthdays, Anniversaries, Premiums Due) ──
    if (filterType === 'NEXT_7') {
        toDate.setDate(today.getDate() + 7);
    }
    else if (filterType === 'NEXT_30') {
        toDate.setDate(today.getDate() + 30);
    }
    // ── PAST_* filters (Birthdays/Anniversaries past) ──
    else if (filterType === 'PAST_7') {
        fromDate.setDate(today.getDate() - 7);
    }
    // ── MONTH filter ──
    else if (filterType === 'MONTH') {
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
        toDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    }
    // ── 6_MONTHS / 1_YEAR could mean different things per table ──
    else if (filterType === '6_MONTHS') {
        if (tableKey === 'life-premiums-due') {
            toDate.setMonth(today.getMonth() + 6); // Forward only
        } else {
            fromDate.setMonth(today.getMonth() - 6); // Symmetric
            toDate.setMonth(today.getMonth() + 6);
        }
    }
    else if (filterType === '1_YEAR') {
        if (tableKey === 'life-premiums-due') {
            toDate.setFullYear(today.getFullYear() + 1); // Forward only
        } else {
            fromDate.setFullYear(today.getFullYear() - 1); // Symmetric
            toDate.setFullYear(today.getFullYear() + 1);
        }
    }
    // ── Outstanding specific symmetric filters ──
    else if (filterType === '30_DAYS') {
        fromDate.setDate(today.getDate() - 30);
        toDate.setDate(today.getDate() + 30);
    }
    else if (filterType === '5_YEARS') {
        fromDate.setFullYear(today.getFullYear() - 5);
        toDate.setFullYear(today.getFullYear() + 5);
    }
    // ── Default fallback ──
    else {
        toDate.setDate(today.getDate() + 30);
    }

    return { from: toInputDate(fromDate), to: toInputDate(toDate) };
};

const createSortHeader = (field, displayName, sortField, sortDirection, handleSort, alignment = 'left') => () => {
    const isSorted = sortField === field;
    const isAsc = sortDirection === 'asc';
    return (
        <div
            className={`cursor-pointer hover:text-brandRed flex items-center w-full ${alignment === 'right' ? 'justify-end' : 'justify-start'}`}
            onClick={() => handleSort(field)}
        >
            {displayName}
            {isSorted && <span className="ml-1">{isAsc ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>}
        </div>
    );
};

// ── TABLE_CONFIG ──────────────────────────────────────────────────────────────

const TABLE_CONFIG = {
    'life-premiums-outstanding': {
        title: 'Life Insurance Premiums Outstanding',
        defaultFilter: '30_DAYS',
        columnOptions: [
            { value: 'PolicyNumber', label: 'Policy Number' },
            { value: 'LifeAssured', label: 'Policy Holder' },
            { value: 'ProductId', label: 'Product' },
            { value: 'PaymentMode', label: 'Mode' },
            { value: 'PolicyStatus', label: 'Status' },
        ],
        exportActions: ['pdf', 'excel', 'whatsapp', 'email'],
        exportUrl: '/dashboard/export-life-policy-entries',
        exportPdfUrl: '/dashboard/export-life-policy-entries-pdf',
        pdfReportType: 'dashboardOutstanding',
        exportFileName: 'PremiumsOutstanding',
        excelColumns: [
            { header: 'Due Date', key: 'FUPDate' },
            { header: 'Policy No.', key: 'PolicyNumber' },
            { header: 'Policy Holder', key: 'LifeAssuredName' },
            { header: 'Product', key: 'ProductName' },
            { header: 'Comm. Date', key: 'CommDate' },
            { header: 'Term', key: 'Term' },
            { header: 'PPT', key: 'PPT' },
            { header: 'Mode', key: 'PaymentMode' },
            { header: 'Premium', key: 'BasicPremium' },
            { header: 'No. of Prem.', key: 'TotalPremiumPaid' },
            { header: 'Total Premium', key: 'SumAssured' },
        ],

        getColumnDefs: (sortField, sortDirection, handleSort) => [
            {
                headerName: 'Due Date', field: 'FUPDate', flex: 1,
                headerComponent: createSortHeader('FUPDate', 'Due Date', sortField, sortDirection, handleSort, 'right'),
                suppressMenu: true,
                cellRenderer: (p) => <span>{formatDate(p.value)}</span>,
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Policy No.', field: 'PolicyNumber', flex: 1,
                headerComponent: createSortHeader('PolicyNumber', 'Policy No.', sortField, sortDirection, handleSort, 'right'),
                suppressMenu: true,
                cellRenderer: (p) => <span style={{ fontWeight: 500 }}>{p.value || 'N/A'}</span>,
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Policy Holder', field: 'LifeAssuredName', flex: 1, suppressMenu: true,
                cellRenderer: (p) => <span style={{ fontWeight: 500 }}>{p.data?.LifeAssuredName || p.data?.LifeAssured || 'N/A'}</span>,
                cellClass: 'ag-left-aligned-cell',
                headerClass: 'ag-left-aligned-header',
            },
            {
                headerName: 'Product', field: 'ProductName', flex: 1, suppressMenu: true,
                cellRenderer: (p) => <span>{p.data?.ProductName || 'N/A'}</span>,
                cellClass: 'ag-left-aligned-cell',
                headerClass: 'ag-left-aligned-header',
            },
            {
                headerName: 'Comm. Date', field: 'CommDate', flex: 1, suppressMenu: true,
                headerComponent: createSortHeader(
                    'CommDate',
                    'Comm. Date',
                    sortField,
                    sortDirection,
                    handleSort,
                    'right'
                ),
                cellRenderer: (p) => <span>{formatDate(p.value)}</span>,
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Term', field: 'Term', width: 80, suppressMenu: true,
                headerComponent: createSortHeader('Term', 'Term', sortField, sortDirection, handleSort, 'right'),
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'PPT', field: 'PPT', width: 80, suppressMenu: true,
                headerComponent: createSortHeader('PPT', 'PPT', sortField, sortDirection, handleSort, 'right'),
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Mode', field: 'PaymentMode', width: 100, suppressMenu: true,
                headerComponent: createSortHeader('PaymentMode', 'Mode', sortField, sortDirection, handleSort, 'right'),
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Premium', field: 'BasicPremium', width: 110, suppressMenu: true,
                headerComponent: createSortHeader('BasicPremium', 'Premium', sortField, sortDirection, handleSort, 'right'),
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'No. of Prem.', field: 'TotalPremiumPaid', width: 120, suppressMenu: true,
                cellRenderer: (p) => {
                    const val = calculateNumberOfPremiums(p.data?.CommDate, p.data?.FUPDate, p.data?.PaymentMode);
                    return <span>{val}</span>;
                },
                headerComponent: createSortHeader('TotalPremiumPaid', 'No. of Prem.', sortField, sortDirection, handleSort, 'right'),
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Total Premium', field: 'SumAssured', width: 130, suppressMenu: true,
                headerComponent: createSortHeader('SumAssured', 'Total Premium', sortField, sortDirection, handleSort, 'right'),
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
        ],
    },

    'life-premiums-due': {
        title: 'Life Insurance Premiums Due',
        defaultFilter: 'NEXT_7',
        columnOptions: [
            { value: 'PolicyNumber', label: 'Policy Number' },
            { value: 'LifeAssured', label: 'Policy Holder' },
            { value: 'PaymentMode', label: 'Mode' },
        ],
        exportActions: ['pdf', 'excel', 'whatsapp', 'email'],
        exportUrl: '/dashboard/export-life-premiums-due',
        exportPdfUrl: '/dashboard/export-life-premiums-due-pdf',
        pdfReportType: 'dashboardPremiumDue',
        exportFileName: 'PremiumsDue',
        excelColumns: [
            { header: 'Due Date', key: 'FUPDate' },
            { header: 'Policy No.', key: 'PolicyNumber' },
            { header: 'Policy Holder', key: 'LifeAssuredName' },
            { header: 'Product', key: 'ProductName' },
            { header: 'Term', key: 'Term' },
            { header: 'PPT', key: 'PPT' },
            { header: 'Mode', key: 'PaymentMode' },
            { header: 'Amount Due', key: 'TotalAmount' },
        ],
        getColumnDefs: (sortField, sortDirection, handleSort) => [
            {
                headerName: 'Due Date', field: 'FUPDate', flex: 1, suppressMenu: true,
                cellRenderer: (p) => <span>{formatDate(p.value)}</span>,
                headerComponent: createSortHeader('FUPDate', 'Due Date', sortField, sortDirection, handleSort, 'right'),
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Policy No.', field: 'PolicyNumber', flex: 1, suppressMenu: true,
                cellRenderer: (p) => <span style={{ fontWeight: 500 }}>{p.value || 'N/A'}</span>,
                headerComponent: createSortHeader('PolicyNumber', 'Policy No.', sortField, sortDirection, handleSort, 'right'),
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Policy Holder', field: 'LifeAssuredName', flex: 1, suppressMenu: true,
                cellRenderer: (p) => <span style={{ fontWeight: 500 }}>{p.data?.LifeAssuredName || 'N/A'}</span>,
                cellClass: 'ag-left-aligned-cell',
                headerClass: 'ag-left-aligned-header',
            },
            {
                headerName: 'Product', field: 'ProductName', flex: 1, suppressMenu: true,
                cellRenderer: (p) => <span>{p.data?.ProductName || 'N/A'}</span>,
                headerComponent: createSortHeader('ProductName', 'Product', sortField, sortDirection, handleSort, 'left'),
                cellClass: 'ag-left-aligned-cell',
                headerClass: 'ag-left-aligned-header',
            },
            {
                headerName: 'Term', field: 'Term', width: 80, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value ?? 'N/A'}</span>,
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'PPT', field: 'PPT', width: 80, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value ?? 'N/A'}</span>,
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Mode', field: 'PaymentMode', width: 100, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value || 'N/A'}</span>,
                cellStyle: { textAlign: 'left' },
            },
            {
                headerName: 'Amount Due', field: 'TotalAmount', width: 130, suppressMenu: true,
                cellRenderer: (p) => (
                    <span style={{ fontWeight: 500 }}>
                        {p.value != null ? Number(p.value).toLocaleString('en-IN') : '0'}
                    </span>
                ),
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
        ],
    },

    'birthdays': {
        title: 'Upcoming Birthdays',
        defaultFilter: 'NEXT_7',
        columnOptions: [
            { value: 'FullName', label: 'Full Name' },
            { value: 'MobileNo', label: 'Mobile No.' },
            { value: 'Email', label: 'Email' },
            { value: 'GroupCode', label: 'Group Code' },
        ],
        exportActions: ['whatsapp', 'excel', 'email'],
        exportUrl: '/dashboard/export-birthdays',
        exportFileName: 'Birthdays',
        excelColumns: [
            { header: 'Full Name', key: 'FullName' },
            { header: 'Mobile No.', key: 'MobileNo' },
            { header: 'Email', key: 'Email' },
            { header: 'Group Code', key: 'GroupCode' },
            { header: 'DOB', key: 'DOB' },
            { header: 'Age', key: '_age' },
        ],

        getColumnDefs: () => [
            {
                headerName: 'Full Name', field: 'FullName', flex: 1, suppressMenu: true,
                cellRenderer: (p) => <span style={{ fontWeight: 500 }}>{p.value || 'N/A'}</span>,
                cellStyle: { textAlign: 'left' },
            },
            {
                headerName: 'Mobile No.', field: 'MobileNo', width: 130, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value || 'N/A'}</span>,
                cellStyle: { textAlign: 'left' },
            },
            {
                headerName: 'Email', field: 'Email', flex: 1, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value || 'N/A'}</span>,
                cellStyle: { textAlign: 'left' },
            },
            {
                headerName: 'Group Code', field: 'GroupCode', width: 120, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value || 'N/A'}</span>,
                cellStyle: { textAlign: 'left' },
            },
            {
                headerName: 'DOB', field: 'DOB', flex: 1, suppressMenu: true,
                cellRenderer: (p) => <span>{formatDate(p.value)}</span>,
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Age', field: 'DOB', width: 80, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value ? new Date().getFullYear() - new Date(p.value).getFullYear() : 'N/A'}</span>,
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
        ],
    },

    'anniversaries': {
        title: 'Upcoming Anniversaries',
        defaultFilter: 'NEXT_7',
        columnOptions: [
            { value: 'FullName', label: 'Full Name' },
            { value: 'MobileNo', label: 'Mobile No.' },
            { value: 'Email', label: 'Email' },
            { value: 'GroupCode', label: 'Group Code' },
        ],
        exportActions: ['whatsapp', 'excel', 'email'],
        exportUrl: '/dashboard/export-anniversaries',
        exportFileName: 'Anniversaries',
        excelColumns: [
            { header: 'Full Name', key: 'FullName' },
            { header: 'Mobile No.', key: 'MobileNo' },
            { header: 'Email', key: 'Email' },
            { header: 'Group Code', key: 'GroupCode' },
            { header: 'Anniversary', key: 'MarriageDate' },
            { header: 'Years Completed', key: '_yearsCompleted' },
        ],
        getColumnDefs: () => [
            {
                headerName: 'Full Name', field: 'FullName', flex: 1, suppressMenu: true,
                cellRenderer: (p) => <span style={{ fontWeight: 500 }}>{p.value || 'N/A'}</span>,
                cellStyle: { textAlign: 'left' },
            },
            {
                headerName: 'Mobile No.', field: 'MobileNo', width: 130, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value || 'N/A'}</span>,
                cellStyle: { textAlign: 'left' },
            },
            {
                headerName: 'Email', field: 'Email', flex: 1, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value || 'N/A'}</span>,
                cellStyle: { textAlign: 'left' },
            },
            {
                headerName: 'Group Code', field: 'GroupCode', width: 120, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value || 'N/A'}</span>,
                cellStyle: { textAlign: 'left' },
            },
            {
                headerName: 'Anniversary', field: 'MarriageDate', flex: 1, suppressMenu: true,
                cellRenderer: (p) => <span>{formatDate(p.value)}</span>,
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Years Completed', field: 'MarriageDate', width: 140, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value ? new Date().getFullYear() - new Date(p.value).getFullYear() : 'N/A'}</span>,
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
        ],
    },

    'general-insurance-renewals': {
        title: 'General Insurance Renewals',
        defaultFilter: 'NEXT_7',
        columnOptions: [
            { value: 'policyNo', label: 'Policy No.' },
            { value: 'policyHolder', label: 'Policy Holder' },
            { value: 'product', label: 'Product' },
            { value: 'mode', label: 'Mode' },
            { value: 'status', label: 'Status' },
        ],
        exportActions: ['pdf', 'excel', 'whatsapp', 'email'],
        exportUrl: '/dashboard/export-general-renewals',
        exportPdfUrl: '/dashboard/export-general-renewals-pdf',
        pdfReportType: 'dashboardGeneralRenewals',
        exportFileName: 'GeneralInsuranceRenewals',
        excelColumns: [
            { header: 'Expiry Date', key: 'expiryDate' },
            { header: 'Policy No.', key: 'policyNo' },
            { header: 'Policy Holder', key: 'policyHolder' },
            { header: 'Product', key: 'product' },
            { header: 'Mode', key: 'mode' },
            { header: 'Premium', key: 'premium' },
            { header: 'Status', key: 'status' },
        ],
        getColumnDefs: (sortField, sortDirection, handleSort) => [
            {
                headerName: 'Expiry Date', field: 'expiryDate', flex: 1, suppressMenu: true,
                headerComponent: createSortHeader('expiryDate', 'Expiry Date', sortField, sortDirection, handleSort, 'right'),
                cellRenderer: (p) => <span>{formatDate(p.value)}</span>,
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Policy No.', field: 'policyNo', flex: 1, suppressMenu: true,
                headerComponent: createSortHeader('policyNo', 'Policy No.', sortField, sortDirection, handleSort, 'right'),
                cellRenderer: (p) => <span style={{ fontWeight: 500 }}>{p.value || 'N/A'}</span>,
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Policy Holder', field: 'policyHolder', flex: 1.2, suppressMenu: true,
                cellRenderer: (p) => <span style={{ fontWeight: 500 }}>{p.value || 'N/A'}</span>,
                cellClass: 'ag-left-aligned-cell',
                headerClass: 'ag-left-aligned-header',
            },
            {
                headerName: 'Product', field: 'product', flex: 0.8, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value || 'N/A'}</span>,
                cellClass: 'ag-left-aligned-cell',
                headerClass: 'ag-left-aligned-header',
            },
            {
                headerName: 'Mode', field: 'mode', width: 100, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value || 'N/A'}</span>,
                cellClass: 'ag-left-aligned-cell',
                headerClass: 'ag-left-aligned-header',
            },
            {
                headerName: 'Premium', field: 'premium', width: 110, suppressMenu: true,
                headerComponent: createSortHeader('premium', 'Premium', sortField, sortDirection, handleSort, 'right'),
                cellRenderer: (p) => <span style={{ fontWeight: 500 }}>{p.value != null ? Number(p.value).toLocaleString('en-IN') : '0'}</span>,
                cellClass: 'ag-right-aligned-cell',
                headerClass: 'ag-right-aligned-header',
            },
            {
                headerName: 'Status', field: 'status', width: 150, suppressMenu: true,
                cellRenderer: (p) => <span>{p.value || 'Pending Intimation'}</span>,
                cellClass: 'ag-left-aligned-cell',
                headerClass: 'ag-left-aligned-header',
            },
        ],
    },
};

const ITEMS_PER_PAGE = 30;

// ── useTableData ──────────────────────────────────────────────────────────────

const useTableData = (
    tableKey, filterType, dateMode, fromDate, toDate,
    currentPage, sortField, sortDirection,
    filterColumn, filterValue, isFilterApplied,
) => {
    const isOutstanding = tableKey === 'life-premiums-outstanding';
    const isDue = tableKey === 'life-premiums-due';
    const isBirthday = tableKey === 'birthdays';
    const isAnniversary = tableKey === 'anniversaries';
    const isGeneralRenewals = tableKey === 'general-insurance-renewals';

    const sharedParams = dateMode === 'filterType'
        ? {
            filterType,
            pageNumber: currentPage,
            pageSize: ITEMS_PER_PAGE,
            sortField: sortField || undefined,
            sortOrder: sortDirection || undefined,
            searchTerm: isFilterApplied ? filterColumn : undefined,
            searchValue: isFilterApplied ? filterValue : undefined,
        }
        : {
            fromDate, toDate,
            pageNumber: currentPage,
            pageSize: ITEMS_PER_PAGE,
            sortField: sortField || undefined,
            sortOrder: sortDirection || undefined,
            searchTerm: isFilterApplied ? filterColumn : undefined,
            searchValue: isFilterApplied ? filterValue : undefined,
        };

    const { data: r1, isLoading: l1, isFetching: f1 } = useGetLifePolicyEntriesQuery(sharedParams, { skip: !isOutstanding });
    const { data: r2, isLoading: l2, isFetching: f2 } = useGetLifePremiumsDueQuery(sharedParams, { skip: !isDue });
    const { data: r3, isLoading: l3, isFetching: f3 } = useGetBirthdaysQuery(sharedParams, { skip: !isBirthday });
    const { data: r4, isLoading: l4, isFetching: f4 } = useGetAnniversariesQuery(sharedParams, { skip: !isAnniversary });
    const { data: r5, isLoading: l5, isFetching: f5 } = useGetGeneralRenewalsQuery(sharedParams, { skip: !isGeneralRenewals });

    const active = {
        'life-premiums-outstanding': { data: r1, loading: l1, fetching: f1 },
        'life-premiums-due': { data: r2, loading: l2, fetching: f2 },
        'birthdays': { data: r3, loading: l3, fetching: f3 },
        'anniversaries': { data: r4, loading: l4, fetching: f4 },
        'general-insurance-renewals': { data: r5, loading: l5, fetching: f5 },
    }[tableKey] ?? { data: null, loading: false, fetching: false };

    const newRows = Array.isArray(active.data?.data) ? active.data.data : [];
    const totalCount = active.data?.totalCount ?? 0;

    const [accumulatedRows, setAccumulatedRows] = useState([]);

    const filterIdentity = useMemo(() => JSON.stringify({
        tableKey, filterType, dateMode, fromDate, toDate,
        sortField, sortDirection, filterColumn,
        filterValue: isFilterApplied ? filterValue : '',
    }), [tableKey, filterType, dateMode, fromDate, toDate, sortField, sortDirection, filterColumn, filterValue, isFilterApplied]);

    const prevIdentityRef = useRef(filterIdentity);
    const lastMergedPageRef = useRef(0);

    useEffect(() => {
        if (active.fetching) return;
        const identityChanged = prevIdentityRef.current !== filterIdentity;
        if (identityChanged) {
            prevIdentityRef.current = filterIdentity;
            lastMergedPageRef.current = 1;
            setAccumulatedRows(newRows);
        } else if (currentPage === 1) {
            setAccumulatedRows(newRows);
            lastMergedPageRef.current = 1;
        } else if (currentPage > lastMergedPageRef.current) {
            lastMergedPageRef.current = currentPage;
            setAccumulatedRows((prev) => [...prev, ...newRows]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active.data, active.fetching]);

    return {
        rows: accumulatedRows,
        totalCount,
        isLoading: active.loading,
        isFetchingMore: active.fetching && currentPage > 1,
        isFetching: active.fetching,  // ← YEH ADD KARO

    };
};

// ── Page ──────────────────────────────────────────────────────────────────────

const DashboardDetailsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const gridRef = useRef(null);
    const token = useSelector((state) => state.auth?.token);

    const tableKey = location.state?.tableKey ?? 'life-premiums-outstanding';
    const incomingFilter = location.state?.filterType ?? null;
    const config = TABLE_CONFIG[tableKey] ?? TABLE_CONFIG['life-premiums-outstanding'];
    const [exportLoading, setExportLoading] = useState({ pdf: false, excel: false });

    const initialDates = getDefaultDates(incomingFilter ?? config.defaultFilter, tableKey);

    const [fromDate, setFromDate] = useState(initialDates.from);
    const [toDate, setToDate] = useState(initialDates.to);
    const [appliedFromDate, setAppliedFromDate] = useState(initialDates.from);
    const [appliedToDate, setAppliedToDate] = useState(initialDates.to);
    const [dateMode, setDateMode] = useState('filterType');

    const [filterColumn, setFilterColumn] = useState(config.columnOptions[0]?.value ?? '');
    const [filterValue, setFilterValue] = useState('');
    const [isFilterApplied, setIsFilterApplied] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isShowLoading, setIsShowLoading] = useState(false);
    const [isCommunicationModalOpen, setIsCommunicationModalOpen] = useState(false);
    const [communicationChannel, setCommunicationChannel] = useState('email');


    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleSort = useCallback((field) => {
        let dir = 'asc';
        if (sortField === field) {
            dir = sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc';
        }
        setSortField(dir ? field : null);
        setSortDirection(dir);
        setCurrentPage(1);
    }, [sortField, sortDirection]);

    const handleFilter = useCallback(() => {
        if (!filterValue.trim()) return;
        setIsFilterApplied(true);
        setCurrentPage(1);
    }, [filterValue]);

    const clearFilters = useCallback(() => {
        setFilterValue('');
        setIsFilterApplied(false);
        setCurrentPage(1);
    }, []);

    const handleShowClick = useCallback(() => {
        setAppliedFromDate(fromDate);
        setAppliedToDate(toDate);

        // Only switch to explicit dateRange mode if the user actually changed the dates
        // from the filter-type defaults. If dates are still the defaults, keep filterType
        // mode so the SP uses its own symmetric window — matching the dashboard card count.
        const defaultDates = getDefaultDates(incomingFilter ?? config.defaultFilter, tableKey);
        const datesAreDefault = fromDate === defaultDates.from && toDate === defaultDates.to;
        if (!datesAreDefault) {
            setDateMode('dateRange');
        }

        setCurrentPage(1);
        setIsShowLoading(true);
    }, [fromDate, toDate, incomingFilter, config.defaultFilter]);

    // ── Selection handler ─────────────────────────────────────────────────────

    const handleSelectionChanged = useCallback(() => {
        if (gridRef.current?.api) {
            const selected = gridRef.current.api.getSelectedRows();
            setSelectedRows(selected || []);
        }
    }, []);

    const handleOpenCommunication = useCallback((channel) => {
        if (selectedRows.length === 0) {
            toast.warn('Please Select any Customers');
            return;
        }
        setCommunicationChannel(channel);
        setIsCommunicationModalOpen(true);
    }, [selectedRows]);


    // ── Excel Export ──────────────────────────────────────────────────────────

    const handleExcelExport = useCallback(async () => {
        if (selectedRows.length > 0) {
            // Client-side export for selected rows
            const cols = config.excelColumns || [];
            const currentYear = new Date().getFullYear();
            const wsData = selectedRows.map(row => {
                const obj = {};
                cols.forEach(col => {
                    let val = row[col.key];
                    // Compute derived columns
                    if (col.key === '_age' && row.DOB) {
                        val = currentYear - new Date(row.DOB).getFullYear();
                    } else if (col.key === '_yearsCompleted' && row.MarriageDate) {
                        val = currentYear - new Date(row.MarriageDate).getFullYear();
                    } else if (col.key === 'TotalPremiumPaid') {
                        val = calculateNumberOfPremiums(row.CommDate, row.FUPDate, row.PaymentMode);
                    }
                    // Format dates
                    if (val && (col.key.toLowerCase().includes('date') || col.key === 'DOB' || col.key === 'MarriageDate' || col.key === 'CommDate' || col.key === 'FUPDate')) {
                        val = formatDate(val);
                    }
                    obj[col.header] = val ?? '';
                });
                return obj;
            });

            const ws = XLSX.utils.json_to_sheet(wsData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Selected Records');
            XLSX.writeFile(wb, `${config.exportFileName || 'Export'}_Selected.xlsx`);
        } else {
            // Backend export with date-range filter
            setExportLoading(prev => ({ ...prev, excel: true }));

            try {
                const params = {
                    fromDate: appliedFromDate,
                    toDate: appliedToDate,
                    ...(dateMode === 'filterType' ? { filterType: incomingFilter ?? config.defaultFilter } : {}),
                };
                if (sortField) {
                    params.sortField = sortField;
                    params.sortOrder = sortDirection || 'asc';
                }
                const queryParams = new URLSearchParams(params).toString();

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api${config.exportUrl}?${queryParams}`,
                    { 
                        method: 'GET', 
                        headers: {
                            'Authorization': `Bearer ${token}`
                        },
                        credentials: 'include' 
                    }
                );

                if (!response.ok) throw new Error('Export failed');

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${config.exportFileName || 'Export'}.xlsx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } catch (error) {
                console.error('Excel Export Error:', error);
                toast.error('Failed to export Excel. Please try again.');
            } finally {
                setExportLoading(prev => ({ ...prev, excel: false }));
            }
        }
    }, [selectedRows, config, appliedFromDate, appliedToDate, dateMode, incomingFilter, token]);

    // ── PDF Report ────────────────────────────────────────────────────────────

    const handlePdfReport = useCallback(async () => {
        setExportLoading(prev => ({ ...prev, pdf: true }));

        try {
            let response;
            if (selectedRows.length > 0) {
                // Direct PDF generation for selected rows
                const payload = {
                    title: config.title,
                    fromDate: appliedFromDate,
                    toDate: appliedToDate,
                    dateMode,
                    reportType: config.pdfReportType,
                    filterType: incomingFilter ?? config.defaultFilter,
                };
                response = await fetch(
                    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/dashboard/generate-pdf-direct`,
                    {
                        method: 'POST',
                        headers: { 
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        credentials: 'include',
                        body: JSON.stringify({ rows: selectedRows, payload })
                    }
                );
            } else {
                // Backend export with date-range filter
                const params = {
                    fromDate: appliedFromDate,
                    toDate: appliedToDate,
                    ...(dateMode === 'filterType' ? { filterType: incomingFilter ?? config.defaultFilter } : {}),
                };
                if (sortField) {
                    params.sortField = sortField;
                    params.sortOrder = sortDirection || 'asc';
                }
                const queryParams = new URLSearchParams(params).toString();

                response = await fetch(
                    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api${config.exportPdfUrl}?${queryParams}`,
                    { 
                        method: 'GET', 
                        headers: {
                            'Authorization': `Bearer ${token}`
                        },
                        credentials: 'include' 
                    }
                );
            }

            if (!response.ok) throw new Error('PDF Export failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${config.exportFileName || 'Export'}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('PDF Export Error:', error);
            toast.error('Failed to export PDF. Please try again.');
        } finally {
            setExportLoading(prev => ({ ...prev, pdf: false }));
        }
    }, [tableKey, config, appliedFromDate, appliedToDate, dateMode, incomingFilter, selectedRows, token]);

    // ── Data ──────────────────────────────────────────────────────────────────

    const { rows, totalCount, isLoading, isFetchingMore, isFetching } = useTableData(
        tableKey,
        incomingFilter ?? config.defaultFilter,
        dateMode,
        appliedFromDate,
        appliedToDate,
        currentPage,
        sortField,
        sortDirection,
        filterColumn,
        filterValue,
        isFilterApplied,
    );

    const hasMoreRows = rows.length < totalCount;
    const handleGridScroll = useCallback(() => setCurrentPage((prev) => prev + 1), []);

    // ── Column defs ───────────────────────────────────────────────────────────

    const columnDefs = useMemo(
        () => config.getColumnDefs(sortField, sortDirection, handleSort),
        [sortField, sortDirection, tableKey],
    );
    useEffect(() => {
        if (isShowLoading && !isFetching && !isLoading) {
            setIsShowLoading(false);
        }
    }, [isFetching, isLoading, isShowLoading]);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="ddp-root">


            <div className="ddp-header">
                <button className="ddp-back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={16} />
                </button>
                <h2 className="ddp-page-title">{config.title}</h2>

                {/* ── Export Icons ── */}
                <div className="ddp-export-actions">
                    {config.exportActions?.includes('pdf') && (
                        <button
                            className="ddp-export-btn ddp-export-btn--pdf"
                            title="Export to PDF"
                            onClick={handlePdfReport}
                            disabled={exportLoading.pdf}   // ← ADD
                        >
                            {exportLoading.pdf
                                ? <span className="ddp-spinner" />   // ← ADD
                                : <FileText size={18} />
                            }
                            <span>{exportLoading.pdf ? 'Exporting…' : 'PDF'}</span>
                        </button>
                    )}

                    {config.exportActions?.includes('excel') && (
                        <button
                            className="ddp-export-btn ddp-export-btn--excel"
                            title="Export to Excel"
                            onClick={handleExcelExport}
                            disabled={exportLoading.excel}   // ← ADD
                        >
                            {exportLoading.excel
                                ? <span className="ddp-spinner" />   // ← ADD
                                : <FileSpreadsheet size={18} />
                            }
                            <span>
                                {exportLoading.excel
                                    ? 'Exporting…'
                                    : `Excel${selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}`
                                }
                            </span>
                        </button>
                    )}
                    {config.exportActions?.includes('email') && (
                        <button 
                            className="ddp-export-btn ddp-export-btn--email" 
                            title="Send Email"
                            onClick={() => handleOpenCommunication('email')}
                        >
                            <Mail size={18} />
                            <span>Email</span>
                        </button>
                    )}

                    {config.exportActions?.includes('whatsapp') && (
                        <button 
                            className="ddp-export-btn ddp-export-btn--whatsapp" 
                            title="Send WhatsApp"
                            onClick={() => handleOpenCommunication('whatsapp')}
                        >
                            <MessageCircle size={18} />
                            <span>WhatsApp</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Date-range filter row */}
            <div className="ddp-filter-bar">
                <span className="ddp-filter-label">Due Date Range from</span>

                <CustomDatePickerDashboard value={fromDate} onChange={setFromDate} label="From date" />

                <span className="ddp-filter-label">to</span>

                <CustomDatePickerDashboard value={toDate} onChange={setToDate} label="To date" />

                <button
                    className="ddp-show-btn"
                    onClick={handleShowClick}
                    disabled={isShowLoading}
                >
                    {isShowLoading ? (
                        <>
                            <span className="ddp-spinner" />
                            Loading...
                        </>
                    ) : (
                        'Show'
                    )}
                </button>

                {!isLoading && (
                    <span className="ddp-record-count">
                        {totalCount} Record(s)
                    </span>
                )}
            </div>

            {/* Grid */}
            <DashboardListView
                filterColumn={filterColumn}
                setFilterColumn={setFilterColumn}
                columnOptions={config.columnOptions}
                filterValue={filterValue}
                setFilterValue={setFilterValue}
                handleFilter={handleFilter}
                clearFilters={clearFilters}
                isFilterApplied={isFilterApplied}
                gridRef={gridRef}
                columnDefs={columnDefs}
                rowData={rows}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                onSelectionChanged={handleSelectionChanged}
                defaultColDef={{ sortable: true, resizable: true, suppressMenu: true }}
                loading={isLoading}
                handleItemsPerPageChange={() => { }}
                totalCount={totalCount}
                showFilters={true}
                onBodyScroll={handleGridScroll}
                isFetchingMore={isFetchingMore}
                hasMoreRows={hasMoreRows}
            />

            {/* Communication Modal */}
            <CommunicationMasterModal
                isOpen={isCommunicationModalOpen}
                onClose={() => setIsCommunicationModalOpen(false)}
                defaultChannel={communicationChannel}
                defaultSelectedIds={selectedRows.map(r => r.CustomerId || r.CustomerID || r.id || r.LifeAssured).filter(id => id != null)}
                defaultTemplateId={
                    tableKey === 'birthdays' ? 14 : 
                    tableKey === 'anniversaries' ? 15 : 
                    null
                }
                defaultTemplateName={
                    tableKey === 'life-premiums-outstanding' ? 'Premium Outstanding Reminder' :
                    tableKey === 'life-premiums-due' ? 'Premium Due Reminder' :
                    tableKey === 'general-insurance-renewals' ? 'General Insurance Renewal Reminder' :
                    null
                }
                isDirectSelection={true}
                policyData={selectedRows.map(r => ({
                    customerId:   r.CustomerId   || r.CustomerID || r.id || r.LifeAssured,
                    customerName: r.LifeAssuredName || r.FullName || r.fullName || '',
                    groupCode:    r.GroupCode    || r.groupCode  || '',
                    mobile:       r.Mobile       || r.mobile     || r.MobileNo || '',
                    email:        r.Email        || r.email      || '',
                    policyNumber: r.PolicyNumber || r.policyNo   || '',
                    dueDate:      r.FUPDate      || r.expiryDate || '',
                    amount:       r.TotalAmount  || r.premium    || r.BasicPremium || '',
                    productName:  r.ProductName  || r.product    || '',
                })).filter(r => r.customerId != null)}
                title={`Send ${communicationChannel === 'email' ? 'Email' : communicationChannel === 'sms' ? 'SMS' : 'WhatsApp'}`}
            />
        </div>
    );
};

export default DashboardDetailsPage;